import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (!token) {
      setStatus('error');
      setMessage('This verification link is missing its token. Please use the link from your email.');
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/verify`, { params: { token } });
        setStatus('success');
        setMessage(res.data?.message || 'Your email has been verified.');
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'This verification link is invalid or has expired.');
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <BrandLogo size={56} />
          <h1 className="mt-4 text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Law Agency 360</h1>
          <p className="text-sm text-slate-400 mt-1">Email verification</p>
        </div>

        <div className="bg-slate-900/70 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl text-center" data-testid="verify-email-card">
          {status === 'loading' && (
            <div data-testid="verify-email-loading">
              <div className="w-12 h-12 mx-auto rounded-full bg-violet-500/20 flex items-center justify-center mb-4">
                <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Verifying your email…</h2>
              <p className="text-sm text-slate-400">Hang tight, this only takes a moment.</p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="verify-email-success">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Email verified</h2>
              <p className="text-sm text-slate-400 mb-6">{message}</p>
              <Link
                to="/auth?mode=signin"
                className="inline-block w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white py-3 rounded-xl font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-violet-600/40"
                data-testid="verify-email-signin-btn"
              >
                Continue to Sign In
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="verify-email-error">
              <div className="w-12 h-12 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                <XCircle className="w-6 h-6 text-red-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">Verification failed</h2>
              <p className="text-sm text-slate-400 mb-6">{message}</p>
              <Link
                to="/auth?mode=signin"
                className="inline-block w-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white py-3 rounded-xl font-semibold transition-all duration-200 hover:shadow-xl hover:shadow-violet-600/40"
                data-testid="verify-email-signin-btn"
              >
                Go to Sign In
              </Link>
            </div>
          )}

          <Link to="/" className="block mt-4 text-sm text-slate-400 hover:text-violet-300" data-testid="verify-email-home-link">
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
